import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchFighter } from '../utils/firebase';
import { fighterEntryDisplayMapping, fighterEntryTooltipMapping, weightClasses } from '../constants';
import { calculateAge } from '../utils/common';
import { Share, X } from 'lucide-react';
import FighterQr from '../components/fighter-qr';

const getDateFromUnknown = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value?.toDate === "function") {
        const date = value.toDate();
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const seconds = value?._seconds ?? value?.seconds;
    if (typeof seconds === "number") {
        const nanos = value?._nanoseconds ?? value?.nanoseconds ?? 0;
        const date = new Date(seconds * 1000 + nanos / 1e6);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getElapsedYearsSince = (date) => {
    const today = new Date();
    let years = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        years -= 1;
    }

    return Math.max(0, years);
};

const getDisplayAge = (fighter) => {
    const birthdate = getDateFromUnknown(fighter?.birthdate ?? fighter?.birthday);
    if (birthdate) {
        const ageFromBirthdate = calculateAge(birthdate);
        return Number.isFinite(ageFromBirthdate) ? ageFromBirthdate : null;
    }

    const baseAge = Number.parseInt(fighter?.age, 10);
    if (!Number.isFinite(baseAge)) {
        return null;
    }

    const createdDate = getDateFromUnknown(fighter?.createdOn ?? fighter?.createdAt);
    if (createdDate) {
        return baseAge + getElapsedYearsSince(createdDate);
    }

    return baseAge;
};

export default function Fighter() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [fighter, setFighter] = useState(null)
    const [totalRecord, setTotalRecord] = useState({})

    const handleClose = () => {
        if (window.history.length > 1) {
            navigate(-1); // Navigate back to the previous page
        } else {
            navigate('/'); // Navigate to the home page if no history
        }
    }

    useEffect(() => {
        if (!!fighter) {
            const totalWin = Object.keys(fighterEntryDisplayMapping).map((entry) => {
                return fighter?.records?.[entry]?.win ?? 0;
            }).reduce((a, b) => {
                return a + (parseInt(b) || 0);
            }, 0)
            const totalLoss = Object.keys(fighterEntryDisplayMapping).map((entry) => {
                return fighter?.records?.[entry]?.loss ?? 0;
            }).reduce((a, b) => {
                return a + (parseInt(b) || 0);
            }, 0)
            const totalDraw = Object.keys(fighterEntryDisplayMapping).map((entry) => {
                return fighter?.records?.[entry]?.draw ?? 0;
            }).reduce((a, b) => {
                return a + (parseInt(b) || 0);
            }, 0)

            setTotalRecord({
                win: totalWin,
                loss: totalLoss,
                draw: totalDraw,
                total: totalWin + totalLoss + totalDraw,
            })
        }
    }, [fighter])

    useEffect(() => {
        (async () => {
            if (location?.state?.id === id) {
                setFighter({
                    ...location?.state,
                })
            } else {
                if (!id) {
                    navigate("/404");
                }
                const fetchedFighter = await fetchFighter({ id: id })
                if (fetchedFighter) {
                    setFighter({
                        ...fetchedFighter.data,
                    })
                } else {
                    navigate("/404");
                }
            }
        })()
    }, [id, location?.state, navigate])

    const ageLabel = getDisplayAge(fighter);
    const genderLabel = fighter?.gender?.toUpperCase() === "M"
        ? "Male"
        : fighter?.gender?.toUpperCase() === "F"
            ? "Female"
            : "??";
    const nationalityLabel = fighter?.nationality ?? "?";
    const headlineParts = [
        ageLabel == null ? null : String(ageLabel),
        genderLabel,
        nationalityLabel,
    ].filter(Boolean);

    return (
        !fighter
            ? <div className='flex-center page-size'>
                <div className="loading loading-lg loading-bars"></div>
            </div>
            :
            <>
                <nav className='fixed top-0 left-0 w-screen'>
                    <div className='w-full p-4 gap-2 flex flex-row justify-end items-center'>
                        <FighterQr url={window.location.href}>
                            <button className="btn btn-ghost btn-md btn-square">
                                <Share size={20} />
                            </button>
                        </FighterQr>
                        <button className="btn btn-ghost btn-md btn-square" onClick={handleClose}>
                            <X />
                        </button>
                    </div>
                </nav>
                <div className='page-size flex-center gap-4 w-auto p-4 min-h-[calc(100vh-7rem)]'>
                    <img className='abs-center fixed z-[-1] blur-lg h-full w-full object-center object-cover opacity-10' src={fighter.fighterPicture} alt="" />
                    <div className='flex-center justify-start h-full gap-4 mt-12 md:m-0 w-full md:w-auto min-w-auto md:min-w-[40rem]'>
                        <div className="w-full h-full flex flex-col md:flex-row justify-center items-center gap-6">
                            <div className='aspect-square h-[10rem] w-[10rem] overflow-clip'>
                                <img className='aspect-square h-full w-full object-center object-cover' src={fighter.fighterPicture} alt="" />
                            </div>
                            <div className="flex-center items-start w-full">
                                <div className='flex-center items-start w-full'>
                                    <p className='font-bold text-2xl'>{fighter.gymNameOrTeamName ?? "???"}</p>
                                    <p className='font-bold text-4xl md:text-6xl'>{fighter.name ?? "???"}</p>
                                    <div className="h-1" />
                                    {!!headlineParts.length && (
                                        <p className='text-lg opacity-80'>
                                            {headlineParts.join(" . ")}
                                        </p>
                                    )}
                                    <p className='text-lg opacity-80'>{fighter.height ?? "?"} cm · {weightClasses[fighter.weightClass] ?? "???"}</p>
                                </div>
                            </div>
                        </div>
                        <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {
                                Object.keys(fighterEntryDisplayMapping).map((entry, index) => {
                                    const record = fighter.records[entry]
                                    return (
                                        <div key={index} className='flex flex-row gap-4 w-full border px-4 py-2'>
                                            <p className='uppercase font-bold w-full'>{fighterEntryDisplayMapping[entry]}</p>
                                            <p className='max-w-6 w-full text-center tooltip' data-tip="Win">{record.win || "0"}</p>
                                            <p className='max-w-6 w-full text-center tooltip' data-tip="Loss">{record.loss || "0"}</p>
                                            <p className='max-w-6 w-full text-center tooltip' data-tip="Draw">{record.draw || "0"}</p>
                                        </div>
                                    )
                                })
                            }
                            <div className='flex flex-row gap-4 w-full border px-4 py-2 md:col-span-2 bg-white text-black'>
                                <p className='text-end uppercase font-bold '>
                                    <span className='hidden md:block'>
                                        {"TOTAL [ Win, Loss, Draw ]"}
                                    </span>
                                    <span className='block md:hidden'>
                                        {"TOTAL [ W, L, D ]"}
                                    </span>
                                </p>
                                <div className="flex-1" />
                                <p className='max-w-6 w-full text-center font-bold tooltip' data-tip="Total Win">{totalRecord.win || "0"}</p>
                                <p className='max-w-6 w-full text-center font-bold tooltip' data-tip="Total Loss">{totalRecord.loss || "0"}</p>
                                <p className='max-w-6 w-full text-center font-bold tooltip' data-tip="Total Draw">{totalRecord.draw || "0"}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
    )
}
